import { Sentry } from '../sentry'

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API ${status}: ${typeof body === 'string' ? body : JSON.stringify(body)}`)
  }

  // Server endpoints return { error, code? } on failure; surface that string
  // when present, falling back to the verbose constructor message.
  get friendlyMessage(): string {
    const b = this.body
    if (
      b &&
      typeof b === 'object' &&
      'error' in b &&
      typeof (b as { error: unknown }).error === 'string'
    ) {
      return (b as { error: string }).error
    }
    return this.message
  }
}

// In production set VITE_API_URL (e.g. https://api.topmeeps.com); in dev
// leave it blank so Vite's proxy in vite.config.ts catches /api/*.
const API_BASE = import.meta.env.VITE_API_URL ?? ''

// Paths the 401-redirect should *not* fire on. Sign-in is the obvious one
// (a wrong-code response is a 401 we want the form to handle). The route
// guard below handles all other 401s.
const SIGN_IN_PATH = '/auth/sign-in'

const baseFetch = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
  const res = await fetch(`${API_BASE}/api${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  const text = await res.text()
  const parsed = text ? JSON.parse(text) : null
  if (!res.ok) {
    if (
      res.status === 401 &&
      path !== SIGN_IN_PATH &&
      typeof window !== 'undefined' &&
      window.location.pathname !== '/sign-in'
    ) {
      const next = encodeURIComponent(window.location.pathname + window.location.search)
      window.location.href = `/sign-in?next=${next}`
    }
    const err = new ApiError(res.status, parsed ?? text)
    // 4xx are expected (validation, permission, not-found) and stay silent.
    // 5xx is a server bug worth alerting on, even if the component handles it.
    if (res.status >= 500) {
      Sentry.captureException(err, { tags: { kind: 'api_5xx', path, method } })
    }
    throw err
  }
  return parsed as T
}

export const api = {
  get: <T>(path: string) => baseFetch<T>('GET', path),
  post: <T>(path: string, body: unknown) => baseFetch<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => baseFetch<T>('PATCH', path, body),
  del: <T>(path: string) => baseFetch<T>('DELETE', path),
}
